import { computed, effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';

import { MenuItems } from '@app/core/shared/layout/sidebar/menu-items';
import { ApplicationNames } from '../../enums/applications.types';
import { Menu } from '../../models/menu';
import { ElementService } from '../../services/element.service';
import { AppStateService } from '../app-state.service';
import { MenuUtils } from '../menu/menu.utils';

@Injectable({
  providedIn: 'root'
})
export class SidebarStateService {
  private readonly appStateService = inject(AppStateService);
  private readonly elementService = inject(ElementService);

  private readonly _currentMenu: WritableSignal<Menu[]> = signal([]);
  private readonly _isExpanded: WritableSignal<boolean> = signal(true);
  private readonly _isLoading: WritableSignal<boolean> = signal(false);

  private readonly _location: Signal<'outsideElement' | 'admin' | 'main' | undefined> = computed(() => {
    return this.appStateService.application()
      ? this.appStateService.application() === ApplicationNames.Elementz
        ? this.appStateService.isOutsideElement()
          ? 'outsideElement'
          : 'main'
        : 'admin'
      : undefined;
  });

  private readonly _includeHelp: Signal<boolean> = computed(() => {
    return this._location() === 'outsideElement';
  });

  private readonly _calculatedMenu: Signal<Menu[]> = computed(() => {
    const location = this._location();
    const element = this.appStateService.element();

    if (location === 'outsideElement') {
      let menu = [];

      menu = [
        {
          menuId: 1,
          icon: 'home',
          name: 'Home',
          description: 'Home',
          navigateUrls: ['elements']
        }
      ];

      if (this.appStateService.isAdmin()) {
        menu = [
          ...menu,
          {
            menuId: 2,
            icon: 'settings_account_box',
            name: 'Administration',
            description: 'Administration',
            navigateUrls: ['administration']
          }
        ];
      }
      menu = [
        ...menu,
        {
          menuId: 3,
          icon: 'logout',
          name: 'Sign Out',
          description: 'Sign Out',
          navigateUrls: ['signout']
        }
      ];
      return menu;
    } else if (location === 'admin') {
      return MenuItems.getMenuItems(element);
    } else {
      return undefined;
    }
  });

  constructor() {
    effect(onCleanup => {
      const currentMenu = this._calculatedMenu();
      const element = this.appStateService.element();
      const site = this.appStateService.transactionSite();
      const user = this.appStateService.user();

      if (currentMenu === undefined && element && user) {
        this._isLoading.set(true);
        const subscription = this.elementService.getElementMenus(element, user.userId).subscribe({
          next: menu => {
            this._currentMenu.set(
              menu
                ? MenuUtils.convertMenuItems(
                    menu,
                    element.elementId,
                    site?.description === 'BASE' ? 'Base' : 'Site'
                  )
                : []
            );
            this._isLoading.set(false);
          },
          error: err => {
            console.error(err);
            this._currentMenu.set([]);
            this._isLoading.set(false);
          }
        });
        onCleanup(() => {
          subscription.unsubscribe();
        });
      } else {
        this._currentMenu.set(currentMenu);
        this._isLoading.set(false);
      }
    });
  }

  get currentMenu(): Signal<Menu[]> {
    return this._currentMenu;
  }

  get isExpanded(): Signal<boolean> {
    return this._isExpanded;
  }

  get isLoading(): Signal<boolean> {
    return this._isLoading;
  }

  get includeHelp(): Signal<boolean> {
    return this._includeHelp;
  }

  setIsExpanded(isExpanded: boolean): void {
    this._isExpanded.set(isExpanded);
  }

  setIsLoading(isLoading: boolean): void {
    this._isLoading.set(isLoading);
  }

  toggleIsExpanded(): void {
    this._isExpanded.update(e => !e);
  }

  reset(): void {
    this._isExpanded.set(true);
    this._isLoading.set(false);
  }
}
